import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useChartComments } from '../../hooks/useChartComments';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import type { ChartComment } from '../../services/ChartCommentService';

// ─── Types for the point-click context ────────────────────────────────────────

export interface CommentPointContext {
  /** Pixel X position relative to the chart container */
  x: number;
  /** Pixel Y position relative to the chart container */
  y: number;
  /** The category label (bar charts) or ISO timestamp string (stock charts) */
  category: string;
  /** The series name that was clicked */
  seriesName: string;
  /** For shift-based charts: the shift name extracted from category, e.g. "A" */
  shiftName?: string;
  /** For shift-based charts: the date extracted from category, e.g. "2026-04-20" */
  shiftDate?: string;
  /** For time-based charts: ISO timestamp */
  timestamp?: string;
}

// ─── Popover component (appears on chart point click) ─────────────────────────

interface ChartCommentPopoverProps {
  chartType: string;
  machineBK?: string;
  point: CommentPointContext;
  onClose: () => void;
  /** Ref to the chart container div for positioning bounds */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const ChartCommentPopover: React.FC<ChartCommentPopoverProps> = ({
  chartType,
  machineBK,
  point,
  onClose,
  containerRef,
}) => {
  const { t } = useTranslation();
  const { user } = useUnifiedAuth();
  const currentUserEmail = user?.email || user?.username || '';
  const { comments, loading, addComment, updateComment, deleteComment } = useChartComments({
    chartType,
    machineBK,
  });
  const [text, setText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  // Filter comments relevant to this point
  const pointComments = useMemo(() => {
    return comments.filter((c) => {
      if (point.timestamp && c.timestamp) {
        // Compare timestamps (same minute)
        const pMs = new Date(point.timestamp).getTime();
        const cMs = new Date(c.timestamp).getTime();
        return Math.abs(pMs - cMs) < 60_000;
      }
      if (point.shiftDate && c.shift_date) {
        // Normalize dates - c.shift_date may be "2026-05-02T00:00:00Z", point.shiftDate is "2026-05-02"
        const cDate = c.shift_date.split('T')[0];
        return cDate === point.shiftDate && (!point.shiftName || c.shift_name === point.shiftName);
      }
      // Fallback: match on category string
      if (c.shift_date && c.shift_name) {
        const cDate = c.shift_date.split('T')[0];
        return point.category.includes(cDate) || point.category.includes(c.shift_name);
      }
      return false;
    });
  }, [comments, point]);

  // Position the popover near the clicked point
  const [style, setStyle] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (!popoverRef.current || !containerRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const popover = popoverRef.current;
    const pw = popover.offsetWidth;
    const ph = popover.offsetHeight;

    let left = point.x - pw / 2;
    let top = point.y - ph - 12; // above the point

    // Flip below if not enough space above
    if (top < 0) {
      top = point.y + 16;
    }
    // Clamp horizontally
    if (left < 4) left = 4;
    if (left + pw > container.width - 4) left = container.width - pw - 4;
    // Clamp vertically
    if (top + ph > container.height - 4) top = container.height - ph - 4;
    if (top < 4) top = 4;

    setStyle({ left, top });
  }, [point.x, point.y, containerRef]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay attaching so the triggering click doesn't immediately close it
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Auto-focus textarea
  useEffect(() => {
    textRef.current?.focus();
  }, []);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [pointComments.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addComment({
        text: text.trim(),
        timestamp: point.timestamp || undefined,
        shift_date: point.shiftDate || undefined,
        shift_name: point.shiftName || undefined,
      });
      setText('');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (c: ChartComment) => {
    setEditingId(c.id);
    setEditText(c.text);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editText.trim()) return;
    setSaving(true);
    try {
      await updateComment(editingId, editText.trim());
      setEditingId(null);
      setEditText('');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleDelete = async (id: string) => {
    await deleteComment(id);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const pointLabel = point.shiftDate
    ? `${point.shiftDate}${point.shiftName ? ` — ${t('comments.shift', 'Shift')} ${point.shiftName}` : ''}`
    : point.timestamp
      ? formatDate(point.timestamp)
      : point.category;

  return (
    <div
      ref={popoverRef}
      style={style}
      className="absolute z-50 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <div className="min-w-0 flex-1">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">
            {t('comments.title', 'Comments')}
          </h4>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">{pointLabel}</p>
        </div>
        <button
          onClick={onClose}
          className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Comment list - Chat style */}
      <div className="max-h-52 overflow-y-auto px-3 py-2 space-y-2 bg-gray-50/50 dark:bg-gray-800/50">
        {loading && (
          <div className="text-center text-xs text-gray-400 py-4">
            {t('comments.loading', 'Loading comments...')}
          </div>
        )}
        {!loading && pointComments.length === 0 && (
          <div className="text-center text-xs text-gray-400 dark:text-gray-500 py-4">
            {t('comments.empty', 'No comments yet. Be the first to comment!')}
          </div>
        )}
        {pointComments.map((c) => {
          const isMe = c.author === currentUserEmail || c.author?.includes(currentUserEmail.split('@')[0]);
          const authorName = c.author?.split('@')[0] || 'Unknown';
          
          return (
            <div 
              key={c.id} 
              className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`group max-w-[85%] ${isMe ? 'order-1' : ''}`}>
                {/* Author name (only for others) */}
                {!isMe && (
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 ml-2">
                    {authorName}
                  </div>
                )}
                
                {editingId === c.id ? (
                  /* Edit mode */
                  <div className="bg-white dark:bg-gray-700 rounded-xl p-2 border border-blue-400 dark:border-blue-500 shadow-sm">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      className="w-full px-2 py-1 text-xs border-0 bg-transparent text-gray-800 dark:text-gray-200 resize-none focus:ring-0 focus:outline-none"
                      autoFocus
                    />
                    <div className="flex gap-1 justify-end border-t border-gray-200 dark:border-gray-600 pt-1 mt-1">
                      <button
                        onClick={handleCancelEdit}
                        className="px-2 py-0.5 text-[10px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {t('comments.cancel', 'Cancel')}
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving || !editText.trim()}
                        className="px-2 py-0.5 text-[10px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded"
                      >
                        {t('comments.save', 'Save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Chat bubble */
                  <div 
                    className={`relative px-3 py-2 rounded-2xl shadow-sm cursor-pointer transition-all
                      ${isMe 
                        ? 'bg-blue-500 text-white rounded-br-md hover:bg-blue-600' 
                        : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-md hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    onClick={() => isMe && handleStartEdit(c)}
                    title={isMe ? t('comments.clickToEdit', 'Click to edit') : ''}
                  >
                    <p className="text-xs whitespace-pre-wrap break-words">{c.text}</p>
                    <div className={`flex items-center gap-2 mt-1 text-[9px] ${isMe ? 'text-blue-100' : 'text-gray-400 dark:text-gray-500'}`}>
                      <span>{formatDate(c.created_at)}</span>
                      {isMe && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                          className="opacity-0 group-hover:opacity-100 hover:text-red-300 transition-opacity"
                          title={t('comments.delete', 'Delete')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={commentsEndRef} />
      </div>

      {/* New comment form - Chat input style */}
      <form onSubmit={handleSubmit} className="px-3 py-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={textRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              placeholder={t('comments.placeholder', 'Type a message...')}
              className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-600 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-600"
              style={{ minHeight: '36px', maxHeight: '80px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (text.trim()) handleSubmit(e);
                }
              }}
            />
          </div>
          <button
            type="submit"
            disabled={saving || !text.trim()}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed rounded-full transition-colors shadow-sm"
          >
            {saving ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Hook to manage popover state from chart components ───────────────────────

export function useChartCommentPopover() {
  const [popoverPoint, setPopoverPoint] = useState<CommentPointContext | null>(null);

  const openPopover = useCallback((point: CommentPointContext) => {
    setPopoverPoint(point);
  }, []);

  const closePopover = useCallback(() => {
    setPopoverPoint(null);
  }, []);

  return { popoverPoint, openPopover, closePopover };
}

export default ChartCommentPopover;
